import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  Stack,
  Divider,
  Paper,
  Avatar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentIcon from "@mui/icons-material/Payment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MediaGallery from "../components/MediaGallery";
import EmptyState from "../../../shared/ui/EmptyState";

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/packages`;

const TourSkeleton = ({ onBack }) => (
  <Box sx={{ bgcolor: "#F7F8FC", minHeight: "100vh" }}>
    <Box sx={{ px: { xs: 2, md: 5 }, pt: 3, pb: 2 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: "text.secondary" }}>
        Back to listings
      </Button>
    </Box>
    <Skeleton variant="rectangular" width="100%" height={400} sx={{ mb: 0 }} />
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 5 }, pt: 4 }}>
      <Skeleton variant="text" width="60%" height={44} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mb: 4 }} />
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 2, borderRadius: 2 }} />
          ))}
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
        </Grid>
      </Grid>
    </Box>
  </Box>
);

/* Timeline step for itinerary */
const ItineraryStep = ({ item, isLast }) => (
  <Box sx={{ display: "flex", gap: 2 }}>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
      <Avatar
        sx={{
          width: 34,
          height: 34,
          bgcolor: "#E50000",
          color: "white",
          fontSize: "0.72rem",
          fontWeight: 700,
        }}
      >
        {item.day}
      </Avatar>
      {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: "divider", mt: 1 }} />}
    </Box>
    <Box sx={{ pb: isLast ? 0 : 3.5, pt: 0.25, flex: 1, minWidth: 0 }}>
      <Typography fontWeight={700} sx={{ mb: 0.75, lineHeight: 1.35, fontSize: "0.95rem" }}>
        {item.title}
      </Typography>
      <Stack spacing={0.6}>
        {(item.startTime || item.endTime) && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AccessTimeIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              {item.startTime ? new Date(item.startTime).toLocaleString() : "—"}
              {item.endTime ? ` – ${new Date(item.endTime).toLocaleString()}` : ""}
            </Typography>
          </Stack>
        )}
        {item.location && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <LocationOnIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">{item.location}</Typography>
          </Stack>
        )}
        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.7 }}>
            {item.description}
          </Typography>
        )}
      </Stack>
    </Box>
  </Box>
);

const ViewPackage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const authUser = JSON.parse(localStorage.getItem("authUser") || "null");

  useEffect(() => {
    const fetchTourDetails = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const response = await fetch(`${API_URL}/${id}`);
        if (!response.ok) throw new Error("Not found");
        const data = await response.json();
        setTour({
          ...data,
          media: {
            ...data.media,
            tourImages: data.media?.tourImages?.filter(Boolean),
            tourVideos: data.media?.tourVideos?.filter(Boolean),
            additionalFiles: data.media?.additionalFiles?.filter(Boolean),
          },
        });
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchTourDetails();
  }, [id]);

  if (loading) return <TourSkeleton onBack={() => navigate(-1)} />;

  if (notFound) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: "auto" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 3, color: "text.secondary" }}>
          Back to listings
        </Button>
        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
          <EmptyState
            icon={<ErrorOutlineIcon />}
            title="Tour package not found"
            description="This tour may have been removed or the link is incorrect."
            action={<Button variant="contained" onClick={() => navigate(-1)}>Go back</Button>}
          />
        </Paper>
      </Box>
    );
  }

  const hasMedia =
    tour.media?.tourImages?.length > 0 || tour.media?.tourVideos?.length > 0;

  return (
    <Box sx={{ bgcolor: "#F7F8FC", minHeight: "100vh" }}>
      {/* Back navigation */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 2, md: 5 },
          py: 1.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          size="small"
          sx={{ color: "text.secondary", borderRadius: 2, fontWeight: 500 }}
        >
          Back to listings
        </Button>
      </Box>

      {/* Full-width gallery — this IS the hero */}
      {hasMedia ? (
        <Box sx={{ width: "100%", bgcolor: "#111" }}>
          <MediaGallery media={tour.media} />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: { xs: 220, md: 360 },
            bgcolor: "#1a1a2e",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MapOutlinedIcon sx={{ fontSize: 64, color: "rgba(255,255,255,0.15)" }} />
        </Box>
      )}

      {/* Page title section — white strip below gallery */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 2, md: 5 },
          py: { xs: 2.5, md: 3.5 },
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {tour.basicInfo?.tour_type && (
            <Chip
              label={tour.basicInfo.tour_type}
              size="small"
              sx={{
                mb: 1.25,
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: "rgba(229,0,0,0.08)",
                color: "#E50000",
                border: "1px solid rgba(229,0,0,0.2)",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            />
          )}
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ mb: 1.25, lineHeight: 1.2, letterSpacing: -0.3 }}
          >
            {tour.basicInfo?.tour_name}
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <LocationOnIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {[tour.basicInfo?.city, tour.basicInfo?.country].filter(Boolean).join(", ")}
              </Typography>
            </Stack>
            {tour.basicInfo?.duration && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CalendarMonthIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {tour.basicInfo.duration}{" "}
                  {Number(tour.basicInfo.duration) === 1 ? "day" : "days"}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Main content + sidebar */}
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 5 }, py: { xs: 3, md: 4 } }}>
        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT: Main content — order 2 on mobile so sidebar shows first */}
          <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
            {/* About */}
            {tour.basicInfo?.description && (
              <Card
                elevation={0}
                sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", mb: 3, bgcolor: "white" }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography fontWeight={700} sx={{ mb: 1.5, fontSize: "1rem" }}>
                    About this experience
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {tour.basicInfo.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Requirements / Highlights */}
            {(tour.additional?.requirements || tour.additional?.tags?.length > 0) && (
              <Card
                elevation={0}
                sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", mb: 3, bgcolor: "white" }}
              >
                <CardContent sx={{ p: 3 }}>
                  {tour.additional?.tags?.length > 0 && (
                    <Box sx={{ mb: tour.additional?.requirements ? 2.5 : 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <LocalOfferIcon sx={{ fontSize: 18, color: "#E50000" }} />
                        <Typography fontWeight={700} sx={{ fontSize: "1rem" }}>
                          Highlights
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        {tour.additional.tags.map((t, i) => (
                          <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircleOutlineIcon
                              sx={{ fontSize: 17, color: "#E50000", mt: 0.15, flexShrink: 0 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {t}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {tour.additional?.requirements && (
                    <>
                      {tour.additional?.tags?.length > 0 && <Divider sx={{ mb: 2.5 }} />}
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: "#E50000" }} />
                        <Typography fontWeight={700} sx={{ fontSize: "1rem" }}>
                          What to know
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {tour.additional.requirements}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Itinerary */}
            <Card
              elevation={0}
              sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", mb: 3, bgcolor: "white" }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                  <MapOutlinedIcon sx={{ fontSize: 18, color: "#E50000" }} />
                  <Typography fontWeight={700} sx={{ fontSize: "1rem" }}>
                    Day-by-day itinerary
                  </Typography>
                </Stack>
                {tour.itinerary?.length > 0 ? (
                  <Box>
                    {tour.itinerary.map((item, idx) => (
                      <ItineraryStep
                        key={idx}
                        item={item}
                        isLast={idx === tour.itinerary.length - 1}
                      />
                    ))}
                  </Box>
                ) : (
                  <EmptyState
                    icon={<MapOutlinedIcon />}
                    title="No itinerary yet"
                    description="The tour operator hasn't added a day-by-day breakdown."
                    py={3}
                  />
                )}
              </CardContent>
            </Card>

            {/* Contact info */}
            {tour.additional?.contact && (
              <Card
                elevation={0}
                sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "white" }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography fontWeight={700} sx={{ mb: 2, fontSize: "1rem" }}>
                    Meet your operator
                  </Typography>
                  <Stack spacing={1.25}>
                    {tour.additional.contact.name && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <PersonIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        <Typography variant="body2">{tour.additional.contact.name}</Typography>
                      </Stack>
                    )}
                    {tour.additional.contact.phone && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <PhoneIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        <Typography variant="body2">{tour.additional.contact.phone}</Typography>
                      </Stack>
                    )}
                    {tour.additional.contact.email && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <EmailIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        <Typography variant="body2">{tour.additional.contact.email}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* RIGHT: Booking widget — order 1 on mobile so it appears ABOVE content */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 24 } }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "white",
                  overflow: "visible",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Price — the most prominent element */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ display: "block", mb: 0.25, textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    From
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.5 }}>
                    <Typography
                      fontWeight={800}
                      sx={{ fontSize: "2rem", lineHeight: 1.1, color: "text.primary" }}
                    >
                      {tour.pricing?.currency} {Number(tour.pricing?.pricePerPerson).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: "block" }}>
                    per person
                  </Typography>

                  {/* Book button */}
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    disableElevation
                    onClick={() => navigate(`/book/${id}?distributor=${authUser?._id}`)}
                    sx={{
                      bgcolor: "#E50000",
                      "&:hover": { bgcolor: "#C00000" },
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: "1rem",
                      mb: 2.5,
                    }}
                  >
                    Book This Tour
                  </Button>

                  <Divider sx={{ mb: 2.5 }} />

                  {/* Availability */}
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ display: "block", mb: 1.25, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}
                  >
                    Availability
                  </Typography>
                  {tour.pricing?.availability?.length > 0 ? (
                    <Stack spacing={1} sx={{ mb: 2.5 }}>
                      {tour.pricing.availability.map((a, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: a.is_available ? "rgba(46,160,67,0.07)" : "rgba(229,0,0,0.06)",
                            borderRadius: 1.5,
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={500}>
                            Up to {a.max_guests} guests
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                bgcolor: a.is_available ? "#2ea043" : "#E50000",
                              }}
                            />
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: a.is_available ? "#2ea043" : "#E50000" }}
                            >
                              {a.is_available ? "Available" : "Full"}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                      Contact operator for availability
                    </Typography>
                  )}

                  <Divider sx={{ mb: 2.5 }} />

                  {/* Payment methods */}
                  {tour.booking?.paymentMethods?.length > 0 && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ display: "block", mb: 1.25, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}
                      >
                        Payment Methods
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {tour.booking.paymentMethods.map((m, i) => (
                          <Chip
                            key={i}
                            label={m.name}
                            icon={<PaymentIcon sx={{ fontSize: "14px !important" }} />}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.72rem", borderRadius: 1 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Cancellation */}
                  {tour.booking?.cancellationPolicy && (
                    <>
                      <Divider sx={{ mb: 2.5 }} />
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.75, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}
                      >
                        Cancellation Policy
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {tour.booking.cancellationPolicy}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ViewPackage;
